import {
    FolderKanban,
    Plus, Search, Trash2,
    X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import useGrandeEcole from '../../stores/useGrandeEcole';

function ProjectModal({ project, contacts, onSave, onClose }) {
  const [form, setForm] = useState({
    titre: project?.titre || '',
    description: project?.description || '',
    contactId: project?.contactId || '',
    statut: project?.statut || 'attente',
    budget: project?.budget || '',
    dateLivraison: project?.dateLivraison || '',
    tags: project?.tags?.join(', ') || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      budget: Number(form.budget) || 0,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/30">
          <h3 className="text-sm font-black uppercase tracking-tighter text-fuchsia-300">
            {project ? 'Modifier le projet' : 'Nouveau projet'}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Titre *</label>
            <input value={form.titre} onChange={e => setForm(f => ({...f, titre: e.target.value}))} required
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none resize-none font-mono" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Contact lié</label>
            <select value={form.contactId} onChange={e => setForm(f => ({...f, contactId: e.target.value}))}
                    className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono">
              <option value="">— Aucun —</option>
              {contacts.map(c => (
                <option key={c._id || c.id} value={c._id || c.id}>
                  {c.prenom} {c.nom} ({c.type_contact || 'contact'})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Statut</label>
              <select value={form.statut} onChange={e => setForm(f => ({...f, statut: e.target.value}))}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="attente">En attente</option>
                <option value="en_cours">En cours</option>
                <option value="livre">Livré</option>
                <option value="annule">Annulé</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Budget (€)</label>
              <input type="number" value={form.budget} onChange={e => setForm(f => ({...f, budget: e.target.value}))}
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Date de livraison</label>
            <input type="date" value={form.dateLivraison} onChange={e => setForm(f => ({...f, dateLivraison: e.target.value}))}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Tags (séparés par virgule)</label>
            <input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-xs text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="flex-1 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-black uppercase tracking-widest">
              {project ? 'Enregistrer' : 'Créer le projet'}
            </button>
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs uppercase tracking-widest">Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CreativeProjectsPanel() {
  const { crmContacts } = useGrandeEcole();
  const [projects, setProjects] = useLocalStorage('creative_projects', []);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(p =>
      p.titre.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q)
    );
  }, [projects, search]);

  const handleSave = (data) => {
    if (editing) {
      setProjects(prev => prev.map(p => p.id === editing.id ? { ...p, ...data, updatedAt: new Date().toISOString() } : p));
    } else {
      const newProject = {
        id: `proj_${Date.now()}`,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setProjects(prev => [newProject, ...prev]);
    }
    setShowModal(false);
    setEditing(null);
  };

  const handleDelete = (id) => setProjects(prev => prev.filter(p => p.id !== id));

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (proj) => { setEditing(proj); setShowModal(true); };

  return (
    <div className="space-y-4 font-mono">
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-widest text-zinc-600">
          {projects.length} projet{projects.length > 1 ? 's' : ''}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un projet…"
              className="w-48 bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none"
            />
          </div>
          <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-2 bg-fuchsia-600/80 hover:bg-fuchsia-600 text-white text-[10px] uppercase tracking-widest font-bold">
            <Plus size={12} /> Nouveau
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 bg-black/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Titre</span>
          <span className="col-span-2">Contact</span>
          <span className="col-span-2">Statut</span>
          <span className="col-span-1">Budget</span>
          <span className="col-span-2">Livraison</span>
          <span className="col-span-1 text-right">···</span>
        </div>
        <div className="divide-y divide-zinc-800/40 max-h-[32rem] overflow-y-auto">
          {filtered.map(proj => {
            const contact = crmContacts.find(c => (c._id || c.id) === proj.contactId);
            return (
              <div key={proj.id} onClick={() => openEdit(proj)}
                   className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-zinc-900/40 cursor-pointer items-center">
                <div className="col-span-4 min-w-0">
                  <div className="text-xs font-bold text-zinc-200 truncate">{proj.titre}</div>
                  {proj.description && <div className="text-[9px] text-zinc-600 truncate mt-0.5">{proj.description}</div>}
                </div>
                <div className="col-span-2 text-[10px] text-zinc-400 truncate">
                  {contact ? `${contact.prenom} ${contact.nom}` : '—'}
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 font-mono ${
                    proj.statut === 'en_cours' ? 'bg-sky-900/50 text-sky-300' :
                    proj.statut === 'livre' ? 'bg-emerald-900/50 text-emerald-300' :
                    proj.statut === 'attente' ? 'bg-amber-900/50 text-amber-300' :
                    'bg-zinc-800 text-zinc-400'
                  }`}>
                    {proj.statut === 'attente' ? '⏳ Attente' : proj.statut === 'en_cours' ? '🎬 En cours' : proj.statut === 'livre' ? '✅ Livré' : proj.statut}
                  </span>
                </div>
                <div className="col-span-1 text-[10px] text-zinc-300 font-mono">{proj.budget || '—'} €</div>
                <div className="col-span-2 text-[10px] text-zinc-500 font-mono">{proj.dateLivraison || '—'}</div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={e => { e.stopPropagation(); handleDelete(proj.id); }} className="p-1 text-zinc-700 hover:text-red-400"><Trash2 size={11} /></button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <FolderKanban size={32} className="text-zinc-800 mx-auto mb-3" />
              <p className="text-zinc-600 text-xs uppercase tracking-widest">Aucun projet</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <ProjectModal
          project={editing}
          contacts={crmContacts}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </div>
  );
}